// frontend/src/components/admin/AuditLogViewer.js
// Admin-only audit trail viewer with server-side pagination and filters.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Link
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const ENTITY_TYPES = ['Complaint', 'User', 'Settings'];

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const AuditLogViewer = () => {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Filter option sources
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);

  // Detail dialog
  const [selectedLog, setSelectedLog] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    api
      .get('/audit-logs/actions')
      .then((res) => setActions(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('Error fetching audit actions:', err));
    api
      .get('/users')
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('Error fetching users:', err));
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize
      };
      if (actionFilter) params.action = actionFilter;
      if (entityTypeFilter) params.entityType = entityTypeFilter;
      if (userFilter) params.performedBy = userFilter;
      if (startDate) params.startDate = startDate.format('YYYY-MM-DD');
      if (endDate) params.endDate = endDate.format('YYYY-MM-DD');

      const res = await api.get('/audit-logs', { params });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setRowCount(res.data?.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  }, [paginationModel, actionFilter, entityTypeFilter, userFilter, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleResetFilters = () => {
    setActionFilter('');
    setEntityTypeFilter('');
    setUserFilter('');
    setStartDate(null);
    setEndDate(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = [
    {
      field: 'createdAt',
      headerName: 'Time',
      width: 180,
      sortable: false,
      valueFormatter: (value) => formatDateTime(value),
      renderCell: (params) => formatDateTime(params.row?.createdAt)
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 170,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row?.action || 'unknown'} size="small" variant="outlined" />
      )
    },
    {
      field: 'entityLabel',
      headerName: 'Entity',
      width: 220,
      sortable: false,
      renderCell: (params) => {
        const { entityType, entityId, entityLabel } = params.row || {};
        const label = entityLabel || entityId || 'N/A';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {entityType || ''}
            </Typography>
            {entityType === 'Complaint' && entityId ? (
              <Link
                component="button"
                underline="hover"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/complaint/${entityId}`);
                }}
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {label}
              </Link>
            ) : (
              <Typography
                variant="body2"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {label}
              </Typography>
            )}
          </Box>
        );
      }
    },
    {
      field: 'performedByName',
      headerName: 'Performed By',
      width: 150,
      sortable: false,
      renderCell: (params) => params.row?.performedByName || 'System'
    },
    {
      field: 'summary',
      headerName: 'Summary',
      flex: 1,
      minWidth: 240,
      sortable: false
    }
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth={false} sx={{ width: '100%', mt: 7, p: 2, bgcolor: 'var(--bg-color)' }}>
        <Typography variant="h5" align="left" sx={{ color: 'var(--text-color)', fontWeight: 600, mb: 2 }}>
          Audit Logs
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {/* Filters */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl variant="outlined" fullWidth size="small">
              <InputLabel id="audit-action-filter-label">Action</InputLabel>
              <Select
                labelId="audit-action-filter-label"
                label="Action"
                value={actionFilter}
                onChange={(e) => handleFilterChange(setActionFilter)(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {actions.map((action) => (
                  <MenuItem key={action} value={action}>
                    {action}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl variant="outlined" fullWidth size="small">
              <InputLabel id="audit-entity-filter-label">Entity Type</InputLabel>
              <Select
                labelId="audit-entity-filter-label"
                label="Entity Type"
                value={entityTypeFilter}
                onChange={(e) => handleFilterChange(setEntityTypeFilter)(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {ENTITY_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl variant="outlined" fullWidth size="small">
              <InputLabel id="audit-user-filter-label">User</InputLabel>
              <Select
                labelId="audit-user-filter-label"
                label="User"
                value={userFilter}
                onChange={(e) => handleFilterChange(setUserFilter)(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => handleFilterChange(setStartDate)(newValue)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => handleFilterChange(setEndDate)(newValue)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button variant="outlined" onClick={handleResetFilters} fullWidth aria-label="Reset Filters">
              Reset
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Paper sx={{ boxShadow: 3, borderRadius: 1 }}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            getRowId={(r) => r._id}
            loading={loading}
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnMenu
            onRowClick={(params) => setSelectedLog(params.row)}
            sx={{
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              border: 'none'
            }}
            localeText={{ noRowsLabel: 'No audit logs found' }}
          />
        </Paper>

        {/* Detail dialog */}
        <Dialog open={Boolean(selectedLog)} onClose={() => setSelectedLog(null)} maxWidth="md" fullWidth>
          <DialogTitle>Audit Log Details</DialogTitle>
          <DialogContent dividers>
            {selectedLog && (
              <Box>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Time</Typography>
                    <Typography variant="body2">{formatDateTime(selectedLog.createdAt)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Action</Typography>
                    <Typography variant="body2">{selectedLog.action || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Entity</Typography>
                    <Typography variant="body2">
                      {(selectedLog.entityType || 'N/A') + ' — ' + (selectedLog.entityLabel || selectedLog.entityId || 'N/A')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Performed By</Typography>
                    <Typography variant="body2">{selectedLog.performedByName || 'System'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Summary</Typography>
                    <Typography variant="body2">{selectedLog.summary || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Batch ID</Typography>
                    <Typography variant="body2">{selectedLog.batchId || '–'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">IP Address</Typography>
                    <Typography variant="body2">{selectedLog.ip || '–'}</Typography>
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Changes
                </Typography>
                {Array.isArray(selectedLog.changes) && selectedLog.changes.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>From</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>To</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedLog.changes.map((change, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{change.field}</TableCell>
                          <TableCell sx={{ wordBreak: 'break-word' }}>
                            {change.from === null || change.from === undefined || change.from === ''
                              ? '–'
                              : String(change.from)}
                          </TableCell>
                          <TableCell sx={{ wordBreak: 'break-word' }}>
                            {change.to === null || change.to === undefined || change.to === ''
                              ? '–'
                              : String(change.to)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No field-level changes recorded.
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {selectedLog?.entityType === 'Complaint' && selectedLog?.entityId && (
              <Button onClick={() => navigate(`/complaint/${selectedLog.entityId}`)}>
                Open Complaint
              </Button>
            )}
            <Button onClick={() => setSelectedLog(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default AuditLogViewer;
